import React from 'react';
import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import type { LanguageCode } from '@/i18n/translations';

interface LanguageSelectorProps {
  /** Hide the leading globe icon (used in compact menu rows). */
  hideIcon?: boolean;
  /** Override the className of the trigger to fit different contexts. */
  triggerClassName?: string;
  /** Optional id, useful for pairing with an external <Label>. */
  id?: string;
}

/**
 * Global language selector. Reads/writes the active language through the
 * LanguageContext, which persists the choice to localStorage and (if signed
 * in) the user's profile row. Switching is instant — no reload required.
 */
const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  hideIcon = false,
  triggerClassName,
  id,
}) => {
  const { language, setLanguage, supported, t } = useLanguage();

  const handleChange = (val: string) => {
    void setLanguage(val as LanguageCode);
  };

  return (
    <div className="flex items-center gap-2">
      {!hideIcon && <Globe className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />}
      <Select value={language} onValueChange={handleChange}>
        <SelectTrigger
          id={id}
          className={triggerClassName ?? 'w-[180px] rounded-full'}
          aria-label={t('Language')}
        >
          <SelectValue placeholder={t('Select a language')} />
        </SelectTrigger>
        <SelectContent>
          {supported.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <span className="font-medium">{lang.native}</span>
              <span className="text-slate-400 text-xs ml-2">{lang.flag}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSelector;
